import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { authLogin, authLogout, authMe, authRegister } from "../api/endpoints/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const isAuthenticated = Boolean(user);

  const refreshMe = useCallback(async () => {
    setIsAuthLoading(true);
    try {
      const data = await authMe();
      const nextUser = data?.user || null;
      setUser(nextUser);
      return nextUser;
    } catch {
      setUser(null);
      return null;
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  const login = useCallback(async (payload) => {
    await authLogin(payload);
    return refreshMe();
  }, [refreshMe]);

  const register = useCallback(async (payload) => {
    await authRegister(payload);
    return refreshMe();
  }, [refreshMe]);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const value = useMemo(
    () => ({
      user,
      isAuthLoading,
      isAuthenticated,
      login,
      register,
      logout,
      refreshMe,
    }),
    [user, isAuthLoading, isAuthenticated, login, register, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};






